import { collectionLessons } from '@platform/client';

import { publishReadinessKey } from '../authoring/readiness';

import type { QueryClient } from '@tanstack/react-query';

/**
 * What a collection holds, asked the same way from every screen that asks.
 *
 * The key is here rather than written out at each call site for the reason `readiness.ts`
 * gives about its own: three screens invalidate it — publishing, reordering, adding — and
 * a key that drifted on one would leave another showing an order the server no longer
 * has, with nothing failing anywhere.
 */
export const membersQuery = (id: string) => ({
  queryKey: membersKey(id),
  queryFn: () => collectionLessons({ path: { id }, throwOnError: true }),
});

const membersKey = (id: string) => ['collection-lessons', id];

/**
 * Everything a change of membership makes stale — GEN-39.
 *
 * The keys were named once and the *set* was still written out per mutation, which is the
 * failure this exists for: readiness is computed from the members, and both membership
 * writes listed the other two keys and not that one. The publish panel went on naming
 * rules belonging to lessons already removed, over a disabled button, until a reload.
 *
 * A fourth query is now one edit rather than three, and a mutation opts out by not
 * calling this — which is what `reorder` does, since no rule sees position.
 */
export const membershipChanged = async (
  queries: QueryClient,
  id: string,
): Promise<void> => {
  await Promise.all([
    queries.invalidateQueries({ queryKey: membersKey(id) }),
    queries.invalidateQueries({ queryKey: publishReadinessKey(id) }),
    queries.invalidateQueries({ queryKey: ['library'] }),
  ]);
};
